import React, { useState,  useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Divider,
  Button,
  ButtonGroup,
} from '@mui/material';

const ResearchAndStudies = () => {
  const [page, setPage] = useState(1);
  useEffect(() => {
  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
}, [page]);


  return (
    <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 8 } }}>
      <Container maxWidth="md">
        {/* Main Title (common to both pages) */}
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: 'primary.main',
            textAlign: 'center',
            mb: 2,
          }}
        >
          Research & Studies
        </Typography>

        {/* ======== PAGE 1 ======== */}
        {page === 1 && (
          <>
            {/* Subtitle */}
            <Typography
              variant="subtitle1"
              component="h2"
              gutterBottom
              sx={{
                textAlign: 'center',
                fontWeight: 500,
                fontSize: '1.2rem',
                color: 'text.secondary',
                mb: 4,
              }}
            >
              A Research Report on the Challenges of Local CSOs in Oromia in Getting Access to Project Funds and Funding Opportunities
            </Typography>

            <Divider sx={{ mb: 4 }} />

            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: 'text.primary',
                textAlign: 'justify',
                whiteSpace: 'pre-line',
              }}
            >
{`NeCSOO planned to contribute to the improvement of Local CSOs’ access to institutional and project funding opportunities through the facilitation of constructive dialogue and interfaces among Local CSOs in Oromia, the regional government, the donor communities, and the private sector organizations.

To support this plan, a rapid assessment has been conducted on the current state of funding opportunities in Oromia aimed at identifying concrete fund access challenges faced by LCSOs in Oromia to serve as an evidence base for constructive dialogue among state and non-state actors at large.

The general objective of the rapid assessment was to establish the current concrete challenges of Local CSOs (LCSOs) in Oromia in terms of getting funds and funding opportunities for the immediate and potential development contributions that they could make in the region. The assessment covered the period of three years from 2022–2024.

The rapid assessment process employed quantitative and qualitative research methods. A quantitative data collection approach was used to gather quantitative data from a sample of LCSOs on the challenges that the LCSOs faced in getting access to project funds and funding opportunities in Oromia. Desk review, key informant interviews, and panel discussions were used to gather qualitative data. The key informants were from key government bodies, CSOs, CSO networks, and donor organizations, while the panel discussion participants were selected from LCSO leaders with in-depth knowledge on the issue.

To help ensure a consistent data collection process, a detailed survey questionnaire was developed and electronically distributed to selected LCSOs (mostly NeCSOO members) to be filled and returned to the consultant. Additionally, 8 different key informant checklists/semi-structured questions were developed for different key informant categories.

The key questions in all cases focused on:
•	Funding opportunities and sources
•	Relevance and responsiveness of donors’ funding to the needs of LCSOs in Oromia
•	Trends of funds raised over the past three years (2022 to 2024)
•	Priorities given to issues in Oromia
•	Competing capacity of LCSOs and their networks in fundraising
•	Challenges of LCSOs and their networks and the causes of the challenges
•	The impact of funding shortage on LCSOs
•	Prospects anticipated for LCSOs in getting access to funds

From the total survey respondent LCSOs, 4 (12%) are registered at the regional level while 29 (88%) are registered at the federal level. Thematic focus-wise, from all 33 respondent LCSOs:
•	90% are engaged in development activities
•	63% are involved in humanitarian response
•	48.5% have been engaged in human rights and governance interventions

In terms of operational areas, the sampled LCSOs are distributed across pastoral, agro-pastoral, and highland areas throughout Oromia.`}
            </Typography>
          </>
        )}

        {/* ======== PAGE 2 ======== */}
        {page === 2 && (
          <>
            {/* Subtitle */}
            <Typography
              variant="subtitle1"
              component="h2"
              gutterBottom
              sx={{
                textAlign: 'center',
                fontWeight: 500,
                fontSize: '1.2rem',
                color: 'text.secondary',
                mb: 2,
              }}
            >
              Preceding on Policy Discussion on the Challenges of Local CSOs for Funding and Partnership Opportunities in the Oromia National Regional State
            </Typography>

            {/* Image right below the subtitle */}
            <Box
              component="img"
              src="/images/policy-discussion.png"
              alt="Policy Discussion Workshop"
              sx={{
                width: '100%',
                borderRadius: 2,
                boxShadow: 3,
                mb: 4,
              }}
            />

            <Divider sx={{ mb: 4 }} />

            <Typography
              variant="body1"
              sx={{
                color: 'text.primary',
                textAlign: 'justify',
                lineHeight: 1.8,
                whiteSpace: 'pre-line',
              }}
            >
{`The Network of Civil Society Organizations in Oromia (NeCSOO), established in November 2009, is the largest non-thematic consortium of civil society organizations in Oromia. The establishment of NeCSOO was prompted by the efforts of 19 prominent civil society organizations (CSOs) that have made important contributions to the growth and well-being of the people of the Oromia National Regional State. Thus, NeCSOO’s vision is to see vibrant and value-driven civil society organizations (CSOs) in Oromia, and its mission is to promote an enabling environment for its members to deliver quality and responsive services and initiatives to and with the people of Oromia.

Currently, NeCSOO secured a project grant through a consortium led by White Hunger Hilfe (WHH), in partnership with the Consortium of Self-Help Group Approach Promoters (CoSAP) and the Development Expertise Center (DEC). The project is titled “Promoting Enabling Environment for CSOs’ Operation in the Oromia Regional State” and is funded by the European Union Delegation to Ethiopia under the “Civil Society Fund Plus (CSF+)” through the broader Capacity Development Program for Local and Grassroots Civil Society Organizations (CDP-Lg CSOs). One of the core components of this project is promoting evidence-based policy dialogue to address the challenges local CSOs face in accessing funding and development partnerships.

Accordingly, a policy dialogue workshop on “Policy Discussion on the Challenges of Local CSOs for Funding and Partnership Opportunities in the Oromia Region” was organized based on a rapid research study conducted by NeCSOO in collaboration with the Oromia Finance Bureau. The workshop took place on May 27, 2025, at Elilly International Hotel, Addis Ababa, with the participation of 43 participants (36 male and 7 female) representing CSOs, government institutions, donor partners, and development actors. The event was marked by active engagement, thoughtful reflections, and constructive dialogue among participants.`}
            </Typography>
          </>
        )}

        {/* Pagination Buttons */}
        <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
          <ButtonGroup variant="outlined" color="primary">
            <Button
              onClick={() => setPage(1)}
              variant={page === 1 ? 'contained' : 'outlined'}
            >
              1
            </Button>
            <Button
              onClick={() => setPage(2)}
              variant={page === 2 ? 'contained' : 'outlined'}
            >
              2
            </Button>
          </ButtonGroup>
        </Box>
      </Container>
    </Box>
  );
};

export default ResearchAndStudies;
